// §7.7.2 Edge support-access audit report.
// Accessible by admin only; shows local audit entries correlated to support sessions.

import { useState, useCallback } from 'react'
import { PageHeader, DataTable, ColumnSettingsButton, colors, input, btnSecondary } from '../../components/ui'
import type { Column } from '../../components/ui'
import { useApiList } from '../../hooks/use-api-list'
import { useTableColumns } from '../../hooks/use-table-columns'

// ── types ─────────────────────────────────────────────────────────────────────

interface SupportAccessRow {
  readonly supportSessionId: string | null
  readonly entityType: string
  readonly entityId: string
  readonly action: string
  readonly changedByName: string | null
  readonly timestamp: string
}

// ── helpers ───────────────────────────────────────────────────────────────────

function formatTimestamp(ts: string | null): string {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return ts
  }
}

// ── styles ────────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '6px',
}

const filterRowStyle: React.CSSProperties = {
  marginBottom: '16px',
}

// ── columns ───────────────────────────────────────────────────────────────────

const columns: readonly Column<SupportAccessRow>[] = [
  {
    key: 'supportSessionId',
    header: 'Session ID',
    render: (r) => <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.supportSessionId ?? '—'}</span>,
    sortable: true,
  },
  {
    key: 'changedByName',
    header: 'Staff Name',
    render: (r) => r.changedByName ?? '—',
    sortable: true,
  },
  {
    key: 'action',
    header: 'Action',
    render: (r) => r.action,
    sortable: true,
  },
  {
    key: 'entityType',
    header: 'Entity Type',
    render: (r) => r.entityType,
    sortable: true,
    filterable: true,
  },
  {
    key: 'entityId',
    header: 'Entity ID',
    render: (r) => <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.entityId}</span>,
  },
  {
    key: 'timestamp',
    header: 'Timestamp',
    render: (r) => formatTimestamp(r.timestamp),
    sortable: true,
  },
]

// ── component ─────────────────────────────────────────────────────────────────

export function SupportAccessReportPage() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const { tableId: _, ...tableColumnProps } = useTableColumns('admin-support-access-report')

  const filters: Record<string, string | undefined> = {
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(to).toISOString() : undefined,
  }

  const {
    data: rows,
    total,
    loading,
    error,
  } = useApiList<SupportAccessRow>({
    path: '/admin/support-access-report',
    limit: 500,
    filters,
  })

  const hasFilters = !!(from || to)

  const clearFilters = useCallback(() => {
    setFrom('')
    setTo('')
  }, [])

  return (
    <div>
      <PageHeader
        title="Support Access Report"
        subtitle={`Audit entries recorded during MedFlow staff support sessions${total > 0 ? ` — ${total.toLocaleString()} rows` : ''}`}
        actions={<ColumnSettingsButton columns={columns} {...tableColumnProps} />}
      />

      <div style={{ padding: '24px' }}>
        {/* Date range filter */}
        <div style={{ ...filterRowStyle, display: 'flex', gap: '16px' }}>
          <div>
            <label style={labelStyle}>From</label>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
        </div>

        {/* Clear filters */}
        {hasFilters && (
          <div style={{ marginBottom: '16px' }}>
            <button style={btnSecondary} onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading...</div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            keyField="supportSessionId"
            emptyMessage="No support access audit entries found."
            {...tableColumnProps}
          />
        )}
      </div>
    </div>
  )
}
